function buildWsUrl(baseUrl: string, ticket?: string | null): string {
  const url = baseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
  const wsBase = url.endsWith('/ws') ? url : `${url}/ws`;
  if (ticket) {
    return `${wsBase}?ticket=${encodeURIComponent(ticket)}`;
  }
  return wsBase;
}

export interface WSClientOptions {
  url: string;
  token?: string;
  ticket?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  deps: {
    issueWsTicket: () => Promise<{ ticket?: string }>;
    ensureSession?: (silent?: boolean) => Promise<string | null>;
    getStoredAccessToken?: () => string | null;
    isNativePlatform: () => boolean;
  };
}

export class WSClient {
  private ws: WebSocket | null = null;
  private wsUrl: string;
  private baseUrl: string;
  private token: string;
  private ticket: string | null = null;
  private onConnectCb?: () => void;
  private onDisconnectCb?: () => void;
  private deps: WSClientOptions['deps'];
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000;
  private shouldReconnect = false;
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();
  private connectionState: 'connecting' | 'connected' | 'disconnected' = 'disconnected';
  private static readonly MAX_RECONNECT_ATTEMPTS_BEFORE_BANNER = 10;
  onConnectionLost?: () => void;
  onConnectionRestored?: () => void;

  constructor(options: WSClientOptions | string) {
    if (typeof options === 'string') {
      this.baseUrl = options;
      this.wsUrl = buildWsUrl(options);
      this.token = '';
      this.deps = {
        issueWsTicket: async () => ({}),
        isNativePlatform: () => false,
      };
      return;
    }
    this.baseUrl = options.url;
    this.wsUrl = buildWsUrl(options.url, options.ticket);
    this.token = options.token || '';
    this.ticket = options.ticket || null;
    this.onConnectCb = options.onConnect;
    this.onDisconnectCb = options.onDisconnect;
    this.deps = options.deps;
  }

  async connect(tokenOrTicket?: string) {
    if (typeof window === 'undefined') return;

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.shouldReconnect = true;
    this.connectionState = 'connecting';

    const isWeb = !this.deps.isNativePlatform();
    const storedToken = this.deps.getStoredAccessToken?.() || '';
    let effectiveToken = tokenOrTicket || this.token || storedToken || '';

    if (isWeb) {
      try {
        const ticketResponse = await this.deps.issueWsTicket();
        if (ticketResponse?.ticket) {
          this.ticket = ticketResponse.ticket;
          effectiveToken = '';
        }
      } catch {
        this.ticket = null;
        effectiveToken = '';
      }
    }

    const wsUrl = buildWsUrl(this.baseUrl, this.ticket);

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (err) {
      console.error('[WS] Failed to create WebSocket:', err);
      this.handleDisconnect();
      return;
    }

    this.ws.onopen = () => {
      this.connectionState = 'connected';
      const wasBeyondBanner = this.reconnectAttempts >= WSClient.MAX_RECONNECT_ATTEMPTS_BEFORE_BANNER;
      this.reconnectAttempts = 0;
      if (wasBeyondBanner) {
        this.onConnectionRestored?.();
      }

      if (!isWeb && effectiveToken) {
        try {
          this.ws?.send(JSON.stringify({ type: 'auth', access_token: effectiveToken }));
        } catch {
          /* ignore */
        }
      }
      if (isWeb && this.ticket) {
        try {
          this.ws?.send(JSON.stringify({ type: 'auth', ticket: this.ticket }));
        } catch {
          /* ignore */
        }
      }

      this.onConnectCb?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string);
        const eventType = data.event ?? data.type;
        if (!eventType) return;

        if (eventType === 'error' && data.data?.code === 4001) {
          this.handleAuthError();
          return;
        }

        const listeners = this.listeners.get(eventType);
        if (listeners) {
          const payload =
            data.data && typeof data.data === 'object'
              ? {
                  ...data.data,
                  ...(data.room_id !== undefined && data.data.room_id === undefined
                    ? { room_id: data.room_id }
                    : {}),
                }
              : (data.data ?? data);
          listeners.forEach((callback) => callback(payload));
        }
      } catch (error) {
        console.error('[WS] Parse error:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };

    this.ws.onclose = (event) => {
      this.connectionState = 'disconnected';
      this.onDisconnectCb?.();
      if (event.code === 4001) {
        this.ticket = null;
      }
      if (this.shouldReconnect) {
        this.attemptReconnect();
      }
    };
  }

  private handleAuthError() {
    this.ticket = null;
    this.disconnect();
    setTimeout(() => this.connect(), 1000);
  }

  private handleDisconnect() {
    this.connectionState = 'disconnected';
    this.onDisconnectCb?.();
    if (this.shouldReconnect) {
      this.attemptReconnect();
    }
  }

  private attemptReconnect() {
    if (!this.shouldReconnect) return;

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    if (this.reconnectAttempts === WSClient.MAX_RECONNECT_ATTEMPTS_BEFORE_BANNER) {
      this.onConnectionLost?.();
    }

    setTimeout(() => {
      void (async () => {
        if (!this.shouldReconnect) return;
        try {
          const fresh = await this.deps.ensureSession?.(false);
          if (fresh && fresh !== '__cookie_session__') {
            this.token = fresh;
          }
        } catch {
          /* ignore */
        }
        const hadTicket = this.ticket;
        this.ticket = null;
        await this.connect(this.token);
        if (!this.isConnected && hadTicket) {
          await this.connect('');
        }
      })();
    }, delay);
  }

  reconnect(token?: string) {
    if (token) this.token = token;
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.ticket = null;
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
    void this.connect(this.token);
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.connectionState = 'disconnected';
    this.ticket = null;
  }

  get readyState() {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  get isConnected() {
    return this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  get isConnecting() {
    return this.connectionState === 'connecting' || this.ws?.readyState === WebSocket.CONNECTING;
  }

  send(event: string, data: Record<string, unknown> = {}, roomId?: number) {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    const payload: Record<string, unknown> = {
      event,
      data,
      ...(roomId !== undefined && { room_id: roomId }),
    };
    try {
      this.ws.send(JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }

  on(type: string, callback: (data: unknown) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);
    return () => this.off(type, callback);
  }

  off(type: string, callback: (data: unknown) => void) {
    this.listeners.get(type)?.delete(callback);
  }
}
